import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { compressImageToMaxBytes, isImageFile } from '../../../lib/imageUtils';

const BrandingSection = ({ data, onChange, onSave, onUploadLogo, onDeleteLogo }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localData, setLocalData] = useState(data);
  const [isSaving, setIsSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(data?.logo);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setLocalData(data);
    setLogoPreview(data?.logo);
    setIsEditing(false);
    setIsSaving(false);
  }, [data]);

  const colorPresets = [
    { name: '딥 블루', primary: '#1B365D', secondary: '#2E5984', accent: '#FF6B35' },
    { name: '포레스트 그린', primary: '#2D5016', secondary: '#4A7C2E', accent: '#FFB84D' },
    { name: '로얄 퍼플', primary: '#4A1B5D', secondary: '#7B2E98', accent: '#FF6B9D' },
    { name: '오션 블루', primary: '#0D4F8B', secondary: '#1E88E5', accent: '#FFA726' }
  ];

  const handleChange = (field, value) => {
    setLocalData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoUpload = async (e) => {
    if (!isEditing || isSaving) return;

    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!isImageFile(file)) {
      alert('이미지 파일만 업로드할 수 있습니다.');
      e.target.value = '';
      return;
    }

    try {
      setIsSaving(true);

      const compressedFile = await compressImageToMaxBytes(file, {
        maxBytes: 1024 * 1024,
        maxDimension: 1024,
        mimeType: 'image/jpeg'
      });

      if (!onUploadLogo) {
        throw new Error('로고 업로드 기능이 준비되지 않았습니다.');
      }

      const previewUrl = URL.createObjectURL(compressedFile);
      setLogoPreview(previewUrl);

      const result = await onUploadLogo(compressedFile);
      setLogoPreview(result?.publicUrl || previewUrl);
      handleChange('logo', result?.publicUrl || '');
      handleChange('logoPath', result?.objectPath || '');
    } catch (err) {
      alert(err?.message || '로고 업로드에 실패했습니다.');
      setLogoPreview(data?.logo);
    } finally {
      setIsSaving(false);
      e.target.value = '';
    }
  };

  const handleLogoDelete = async () => {
    if (!isEditing || isSaving) return;
    if (!logoPreview) return;

    const ok = window.confirm('로고를 삭제할까요?');
    if (!ok) return;

    try {
      setIsSaving(true);
      if (onDeleteLogo) {
        await onDeleteLogo();
      }
      setLogoPreview('');
      handleChange('logo', '');
      handleChange('logoPath', '');
    } catch (err) {
      alert(err?.message || '로고 삭제에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleColorPresetSelect = (preset) => {
    setLocalData(prev => ({
      ...prev,
      primaryColor: preset?.primary,
      secondaryColor: preset?.secondary,
      accentColor: preset?.accent
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    await onSave(localData);
    setIsSaving(false);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalData(data);
    setLogoPreview(data?.logo);
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-accent/10 rounded-md flex items-center justify-center">
            <Icon name="Palette" size={20} color="var(--color-accent)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              브랜딩
            </h2>
            <p className="text-sm caption text-muted-foreground">
              시설의 브랜드 아이덴티티를 설정합니다
            </p>
          </div>
        </div>
        
        {!isEditing && (
          <Button
            variant="outline"
            size="default"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            수정
          </Button>
        )}
      </div>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            로고
          </label>
          <div className="flex flex-col md:flex-row items-start gap-4">
            <div className="w-32 h-32 bg-muted rounded-md flex items-center justify-center overflow-hidden border border-border">
              {logoPreview ? (
                <Image 
                  src={logoPreview} 
                  alt="Facility logo preview showing brand identity and visual representation"
                  className="w-full h-full object-contain"
                />
              ) : (
                <Icon name="Image" size={48} className="text-muted-foreground" />
              )}
            </div>
            
            {isEditing && (
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  iconName="Upload"
                  iconPosition="left"
                  onClick={() => fileInputRef?.current?.click()}
                  disabled={isSaving}
                >
                  로고 업로드
                </Button>
                {logoPreview && (
                  <div className="mt-2">
                    <Button
                      variant="outline"
                      iconName="Trash2"
                      iconPosition="left"
                      onClick={handleLogoDelete}
                      disabled={isSaving}
                    >
                      로고 삭제
                    </Button>
                  </div>
                )}
                <p className="text-sm caption text-muted-foreground mt-2">
                  권장 크기: 512x512px, PNG 또는 JPG 형식
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <label className="block text-sm font-medium text-foreground">
            컬러 테마
          </label>
          
          {isEditing && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              {colorPresets?.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => handleColorPresetSelect(preset)}
                  className="p-3 rounded-md border border-border hover:border-primary transition-athletic active-press"
                >
                  <div className="flex gap-2 mb-2">
                    <div 
                      className="w-8 h-8 rounded"
                      style={{ backgroundColor: preset?.primary }}
                    />
                    <div 
                      className="w-8 h-8 rounded"
                      style={{ backgroundColor: preset?.secondary }}
                    />
                    <div 
                      className="w-8 h-8 rounded"
                      style={{ backgroundColor: preset?.accent }}
                    />
                  </div>
                  <p className="text-sm font-medium text-foreground text-left">
                    {preset?.name}
                  </p>
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Input
                label="주 색상"
                type="color"
                value={localData?.primaryColor}
                onChange={(e) => handleChange('primaryColor', e?.target?.value)}
                disabled={!isEditing}
              />
              <div className="mt-2 text-sm data-text text-muted-foreground">
                {localData?.primaryColor}
              </div>
            </div>
            
            <div>
              <Input
                label="보조 색상"
                type="color"
                value={localData?.secondaryColor}
                onChange={(e) => handleChange('secondaryColor', e?.target?.value)}
                disabled={!isEditing}
              />
              <div className="mt-2 text-sm data-text text-muted-foreground">
                {localData?.secondaryColor}
              </div>
            </div>
            
            <div>
              <Input
                label="강조 색상"
                type="color"
                value={localData?.accentColor}
                onChange={(e) => handleChange('accentColor', e?.target?.value)}
                disabled={!isEditing}
              />
              <div className="mt-2 text-sm data-text text-muted-foreground">
                {localData?.accentColor}
              </div>
            </div>
          </div>
        </div>

        <Input
          label="환영 메시지"
          type="text"
          placeholder="시설 이용을 환영합니다"
          value={localData?.welcomeMessage}
          onChange={(e) => handleChange('welcomeMessage', e?.target?.value)}
          disabled={!isEditing}
          description="공개 세션 페이지에 표시될 메시지"
        />

        <div className="bg-muted rounded-md p-4 md:p-6">
          <h3 className="text-sm font-medium text-foreground mb-3">미리보기</h3>
          <div 
            className="bg-card rounded-md p-4 border"
            style={{ 
              borderColor: localData?.primaryColor,
              backgroundColor: `${localData?.primaryColor}05`
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              {logoPreview && (
                <div className="w-12 h-12 rounded-md overflow-hidden">
                  <Image 
                    src={logoPreview} 
                    alt="Logo preview in branding section showing facility brand identity"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              <div>
                <h4 className="font-heading font-semibold" style={{ color: localData?.primaryColor }}>
                  {localData?.welcomeMessage || '환영 메시지'}
                </h4>
                <p className="text-sm caption text-muted-foreground">
                  공개 세션 페이지 상단에 표시됩니다
                </p>
              </div>
            </div>
            <Button
              variant="default"
              size="sm"
              style={{ 
                backgroundColor: localData?.accentColor,
                color: '#FFFFFF'
              }}
            >
              참가 신청
            </Button>
          </div>
        </div>

        {isEditing && (
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              fullWidth
            >
              취소
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              loading={isSaving}
              iconName="Save"
              iconPosition="left"
              fullWidth
            >
              저장
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandingSection;